import React, { useEffect, useState } from "react";

// Animated semicircular speedometer gauge (SVG).
// value: 0..100 (percent). variant: "blue" | "gold".
export default function Gauge({
  value = 0,
  size = 150,
  label = "",
  sublabel = "",
  variant = "blue",
}) {
  const [v, setV] = useState(0);

  // Animate to target after mount / value change (visual only, no loop).
  useEffect(() => {
    const target = Math.max(0, Math.min(100, value));
    const id = requestAnimationFrame(() => setV(target));
    return () => cancelAnimationFrame(id);
  }, [value]);

  const w = size;
  const h = size * 0.62;
  const cx = w / 2;
  const cy = h - 6;
  const r = w / 2 - 14;

  // Semicircle path (left -> right, over the top).
  const start = polar(cx, cy, r, 180);
  const end = polar(cx, cy, r, 0);
  const arc = `M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`;

  // Needle angle: 180° (left) at 0%, 0° (right) at 100%.
  const angle = 180 - (v / 100) * 180;
  const needle = polar(cx, cy, r - 10, angle);

  const gid = `g-${variant}`;
  const tickAngles = [180, 144, 108, 72, 36, 0];

  return (
    <div className="gauge" style={{ width: w }}>
      <svg width={w} height={h + 4} viewBox={`0 0 ${w} ${h + 4}`}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="0">
            {variant === "gold" ? (
              <>
                <stop offset="0" stopColor="#ffd45e" />
                <stop offset="1" stopColor="#f5a623" />
              </>
            ) : (
              <>
                <stop offset="0" stopColor="#2ea8ff" />
                <stop offset="0.6" stopColor="#1e73ff" />
                <stop offset="1" stopColor="#ffc01e" />
              </>
            )}
          </linearGradient>
        </defs>

        {/* track */}
        <path d={arc} fill="none" stroke="rgba(120,160,255,0.13)" strokeWidth="10" strokeLinecap="round" />

        {/* ticks */}
        {tickAngles.map((a, i) => {
          const o = polar(cx, cy, r + 1, a);
          const inn = polar(cx, cy, r - 7, a);
          return <line key={i} x1={o.x} y1={o.y} x2={inn.x} y2={inn.y} stroke="rgba(180,200,255,0.25)" strokeWidth="1.5" />;
        })}

        {/* value arc */}
        <path
          className="gauge-fill"
          d={arc}
          fill="none"
          stroke={`url(#${gid})`}
          strokeWidth="10"
          strokeLinecap="round"
          pathLength="100"
          strokeDasharray="100"
          strokeDashoffset={100 - v}
        />

        {/* needle */}
        <line
          className="gauge-needle"
          x1={cx}
          y1={cy}
          x2={needle.x}
          y2={needle.y}
          stroke={variant === "gold" ? "#ffd45e" : "#2ea8ff"}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r="6" fill="#0a1124" stroke={variant === "gold" ? "#ffd45e" : "#2ea8ff"} strokeWidth="2.5" />
      </svg>

      <div className="gauge-center">
        <div className="gauge-value">{Math.round(v)}%</div>
        {label ? <div className="gauge-label">{label}</div> : null}
      </div>
      {sublabel ? <div className="gauge-sub">{sublabel}</div> : null}
    </div>
  );
}

function polar(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
}
